import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle } from 'lucide-react';
import usePaymentSettings from '../hooks/usePaymentSettings';

const PaymentProcessingModal = ({ isOpen, onClose, appointment, onSave }) => {
  const { allowsPartialPayment, depositPercentage, provider } = usePaymentSettings();
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'deposit'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && appointment) {
      setPaymentOption('full');
      setError('');
    }
  }, [isOpen, appointment]);

  const getTotalPrice = () => {
    const totalPrice = parseFloat(appointment?.total_price || 0);
    const discountAmount = parseFloat(appointment?.discount_amount || 0);
    const refundAmount = parseFloat(appointment?.refund_amount || 0);
    return totalPrice - discountAmount - refundAmount;
  };

  const getPaymentAmount = () => {
    const total = getTotalPrice();
    if (paymentOption === 'deposit' && allowsPartialPayment) {
      return (total * depositPercentage / 100).toFixed(2);
    }
    return total.toFixed(2);
  };

  const getRemainingAmount = () => {
    if (paymentOption === 'deposit' && allowsPartialPayment) {
      const total = getTotalPrice();
      return (total * (100 - depositPercentage) / 100).toFixed(2);
    }
    return '0.00';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setLoading(true);
    try {
      await onSave({
        paymentStatus: paymentOption === 'full' ? 'paid' : 'partial',
        paymentMethod: provider,
        amountPaid: getPaymentAmount(),
        paymentTransactionId: `${provider.toUpperCase()}_${Date.now()}`, // Demo transaction ID
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-green-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-green-100">
              <CreditCard className="text-green-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Process Payment</h2>
              <p className="text-sm text-gray-600">
                Appointment #{appointment?.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Pricing Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Original Price:</span>
              <span className="font-semibold">${parseFloat(appointment?.total_price || 0).toFixed(2)}</span>
            </div>
            {appointment?.discount_amount > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Discount:</span>
                <span className="font-semibold">-${parseFloat(appointment.discount_amount).toFixed(2)}</span>
              </div>
            )}
            {appointment?.refund_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Refund:</span>
                <span className="font-semibold">-${parseFloat(appointment.refund_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-900">Total Due:</span>
              <span className="font-bold text-primary-600">
                ${getTotalPrice().toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment Options */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Select Payment Option</h3>
            
            {/* Pay Full Amount */}
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
              <input
                type="radio"
                name="paymentOption"
                value="full"
                checked={paymentOption === 'full'}
                onChange={(e) => setPaymentOption(e.target.value)}
                className="mt-1 w-4 h-4 text-primary-600"
              />
              <div className="ml-3 flex-1">
                <div className="font-semibold">Pay Full Amount</div>
                <div className="text-sm text-gray-600">
                  Charge ${getTotalPrice().toFixed(2)} via {provider}
                </div>
              </div>
            </label>

            {/* Pay Deposit (if allowed) */}
            {allowsPartialPayment && (
              <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
                <input
                  type="radio"
                  name="paymentOption"
                  value="deposit"
                  checked={paymentOption === 'deposit'}
                  onChange={(e) => setPaymentOption(e.target.value)}
                  className="mt-1 w-4 h-4 text-primary-600"
                />
                <div className="ml-3 flex-1">
                  <div className="font-semibold">Charge Deposit ({depositPercentage}%)</div>
                  <div className="text-sm text-gray-600">
                    Charge ${(getTotalPrice() * depositPercentage / 100).toFixed(2)} now, 
                    ${getRemainingAmount()} due at appointment
                  </div>
                </div>
              </label>
            )}
          </div>

          {/* Payment Summary */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Amount to Charge:</span>
              <span className="text-2xl font-bold text-green-600">
                ${getPaymentAmount()}
              </span>
            </div>
            <div className="text-xs text-green-700 mt-2">
              Payment Method: {provider.toUpperCase()}
            </div>
          </div>

          {/* Demo Warning */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Demo Mode:</strong> This is a demonstration. No actual payment will be processed.
              In production, you would integrate with {provider} to process real payments.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="text-red-600" size={20} />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Process Payment'}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentProcessingModal;
